import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BarChart3, CalendarDays, Banknote, ClipboardList, ShieldCheck, Mail, Loader2 } from "lucide-react";

export default function Reports() {
  const t = useT();
  const { user } = useAuth();
  const canEmail = ["super_admin", "owner_director", "operations_admin"].includes(user?.role);
  const year = new Date().getFullYear();
  const [seasonal, setSeasonal] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [missing, setMissing] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);

  const emailOwners = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/reports/email-owners");
      const sent = (data.results || []).filter(r => r.ok).map(r => r.email);
      const failed = (data.results || []).filter(r => !r.ok);
      if (data.ok && !failed.length) {
        toast.success(t("reports.email.sent", { emails: sent.join(", ") }));
      } else if (sent.length) {
        toast.warning(t("reports.email.partial", { emails: sent.join(", ") }));
      } else {
        toast.error(failed[0]?.message || t("reports.email.failed"));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("reports.email.failed"));
    } finally {
      setEmailing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, r, m, a] = await Promise.all([
          api.get("/reports/seasonal-allocation", { params: { year } }),
          api.get("/reports/revenue", { params: { year } }).catch(() => ({ data: null })),
          api.get("/reports/missing-logs"),
          api.get("/reports/audit").catch(() => ({ data: null })),
        ]);
        setSeasonal(s.data); setRevenue(r.data); setMissing(m.data); setAudit(a.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [year]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("reports.season", { year })}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("reports.title")}</h2>
        </div>
        {canEmail && (
          <Button
            onClick={emailOwners} disabled={emailing}
            data-testid="email-owners-report-btn"
            className="bg-[#10B981] hover:bg-[#059669] text-white"
          >
            {emailing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            {emailing ? t("reports.email.sending") : t("reports.email.button")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Seasonal allocation */}
        <div className="ledger-card p-5 lg:col-span-2" data-testid="seasonal-allocation">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays className="w-4 h-4 text-[#4DA6FF]" />
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("reports.seasonal.heading")}</div>
          </div>
          <h3 className="font-display text-lg font-bold mb-4">{t("reports.seasonal.sub", { start: seasonal?.window_start || "—", end: seasonal?.window_end || "—" })}</h3>
          {loading ? <div className="h-24 bg-[#131C31] animate-pulse rounded-md" /> :
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {seasonal?.owners?.map(o => (
                <div key={o.owner_id} className="p-4 bg-[#050A14] border border-[#1E293B] rounded-md">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-display font-bold">{o.owner_name}</div>
                    {o.meets_14_day_minimum
                      ? <span className="badge-pill badge-approved"><ShieldCheck className="w-3 h-3" /> {t("reports.seasonal.metDays")}</span>
                      : <span className="badge-pill badge-pending">{t("reports.seasonal.belowDays")}</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <Mini label={t("reports.seasonal.days")} value={o.total_days} />
                    <Mini label={t("reports.seasonal.weekends")} value={o.weekend_days} />
                    <Mini label={t("reports.seasonal.longest")} value={o.longest_consecutive_block} />
                  </div>
                </div>
              ))}
            </div>
          }
        </div>

        {/* Revenue */}
        <div className="ledger-card p-5" data-testid="revenue-report">
          <div className="flex items-center gap-2 mb-4">
            <Banknote className="w-4 h-4 text-[#34D399]" />
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("reports.revenue.heading")}</div>
          </div>
          <div className="font-display text-3xl font-bold tracking-tight mb-3">€ {revenue?.total_revenue?.toFixed?.(0) || 0}</div>
          <div className="text-xs text-[#94A3B8] mb-3">{t("reports.revenue.count", { count: revenue?.trip_count || 0 })}</div>
          <div className="space-y-2">
            {Object.entries(revenue?.by_type || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs border-b border-[#1E293B] py-2">
                <span>{t(`reservation.${k}`)}</span><span className="font-mono">€ {v.toFixed(0)}</span>
              </div>
            ))}
            {(!revenue || Object.keys(revenue.by_type || {}).length === 0) && <div className="text-xs text-[#94A3B8]">{t("reports.revenue.none")}</div>}
          </div>
        </div>

        {/* Missing logs */}
        <div className="ledger-card p-5" data-testid="missing-logs-report">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-4 h-4 text-[#F87171]" />
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("reports.missing.heading")}</div>
          </div>
          <div className="font-display text-3xl font-bold tracking-tight mb-2">{missing?.count || 0}</div>
          <div className="text-xs text-[#94A3B8] mb-3">{t("reports.missing.sub")}</div>
          {missing?.missing?.slice(0, 5).map(r => (
            <div key={r.id} className="text-xs p-2 bg-[#050A14] border border-[#1E293B] rounded-md mb-1">
              <div className="font-medium">{t(`reservation.${r.reservation_type}`)} · {r.client_name || t("reservation.private")}</div>
              <div className="font-mono text-[10px] text-[#94A3B8]">{r.start_date} → {r.end_date}</div>
            </div>
          ))}
        </div>

        {/* Audit */}
        <div className="ledger-card p-5 lg:col-span-2" data-testid="audit-report">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-[#A78BFA]" />
            <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("reports.audit.heading")}</div>
          </div>
          {audit?.events?.length ? (
            <div className="max-h-72 overflow-y-auto divide-y divide-[#1E293B] font-mono text-[11px]">
              {audit.events.slice(0, 40).map(e => (
                <div key={e.id} className="py-2 grid grid-cols-12 gap-2">
                  <div className="col-span-3 text-[#94A3B8]">{new Date(e.created_at).toLocaleString("en-GB", {day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"})}</div>
                  <div className="col-span-2 text-[#4DA6FF]">{e.action_type}</div>
                  <div className="col-span-3">{e.entity_type}</div>
                  <div className="col-span-4 truncate text-[#94A3B8]">{e.entity_id?.slice(0, 8)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-[#94A3B8]">{t("reports.audit.limited")}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-[#0B1221] border border-[#1E293B] rounded-md py-2">
      <div className="font-mono text-[9px] tracking-widest text-[#94A3B8]">{label}</div>
      <div className="font-display text-xl font-bold">{value}</div>
    </div>
  );
}
